import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, FormHelperText } from '@mui/material';
import { FieldInput } from './styles';
import { loadBrazilianMunicipalities } from '../../api/ibgeMunicipalities';

const UFS = 'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' ');

export const parseNaturalidade = (value = '') => {
  const text = String(value || '');
  const match = text.match(/^(.*?)(?:\||\/|\s-\s)\s*([a-z]{2})$/i);
  if (match && UFS.includes(match[2].toUpperCase())) {
    return { cidade: match[1], uf: match[2].toUpperCase() };
  }
  return { cidade: text, uf: null };
};

const normalize = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

export function NaturalidadeField({ value, onChange, error, isBrazilian = true, id }) {
  const { cidade, uf } = parseNaturalidade(value);
  const [municipalities, setMunicipalities] = useState([]);
  const [municipalitiesError, setMunicipalitiesError] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!isBrazilian) return undefined;
    let active = true;
    loadBrazilianMunicipalities()
      .then((rows) => {
        if (!active) return;
        setMunicipalities(rows);
        setMunicipalitiesError(false);

        if (!uf && cidade.trim()) {
          const matches = rows.filter((row) => normalize(row.cidade) === normalize(cidade));
          if (matches.length === 1) onChangeRef.current(`${matches[0].cidade}/${matches[0].uf}`);
        }
      })
      .catch(() => {
        if (active) setMunicipalitiesError(true);
      });
    return () => { active = false; };
  }, [cidade, isBrazilian, uf]);

  const selectedMunicipality = useMemo(
    () => municipalities.find((row) => row.uf === uf && normalize(row.cidade) === normalize(cidade)) || null,
    [cidade, municipalities, uf]
  );

  const update = (nextCity, nextUf) => {
    onChange(nextUf && nextCity.trim() ? `${nextCity}/${nextUf}` : nextCity);
  };

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 104px', gap: 1 }}>
        <Autocomplete id={id}
          freeSolo={!isBrazilian || municipalitiesError}
          options={municipalities}
          value={selectedMunicipality}
          inputValue={cidade}
          getOptionLabel={(option) => typeof option === 'string' ? option : `${option.cidade}/${option.uf}`}
          isOptionEqualToValue={(option, current) => option.id === current.id}
          onInputChange={(_, nextCity, reason) => {
            if (reason === 'input' || reason === 'clear') update(nextCity, null);
          }}
          onChange={(_, municipality) => {
            if (municipality && typeof municipality !== 'string') {
              update(municipality.cidade, municipality.uf);
            }
          }}
          noOptionsText="Município não encontrado"
          renderInput={(params) => (
            <FieldInput
              {...params}
              fullWidth
              required
              placeholder="Busque a cidade"
              inputProps={{ ...params.inputProps, 'aria-label': 'Cidade natal', 'aria-describedby': 'naturalidade-ajuda' }}
              error={Boolean(error)}
            />
          )}
        />
        <Autocomplete
          options={UFS}
          value={uf}
          onChange={(_, nextUf) => update(cidade, nextUf)}
          disabled={!cidade.trim() || !isBrazilian}
          noOptionsText="UF não encontrada"
          renderInput={(params) => (
            <FieldInput
              {...params}
              placeholder="UF"
              inputProps={{ ...params.inputProps, 'aria-label': 'UF natal', 'aria-describedby': 'naturalidade-ajuda' }}
            />
          )}
        />
      </Box>
      <FormHelperText id="naturalidade-ajuda" error={Boolean(error)} sx={{ mx: 0, mt: 0.75 }}>
        {error
          ? (isBrazilian ? 'Selecione a cidade e a UF de nascimento.' : 'Informe a cidade de nascimento.')
          : municipalitiesError
            ? 'Busca de municípios indisponível. Informe a cidade e selecione a UF.'
            : (isBrazilian ? 'Busque e selecione o município de nascimento.' : 'Informe a cidade de nascimento.')}
      </FormHelperText>
    </Box>
  );
}
